<template>
  <div
    class="text-video"
    :class="direction"
  >
    <div class="text">
      <UiComponentHeading
        as="paragraph"
        :level="level"
      >
        <slot name="heading" />
      </UiComponentHeading>
      <UiComponentParagraph
        v-if="hasParagraph"
        class="description"
      >
        <slot name="paragraph" />
      </UiComponentParagraph>
      <renderNode v-if="node" />
      <AtomButton
        v-if="hasButton"
        :action="button.action"
        :class="button.class"
        :label="button.label"
        size="l"
        :to="button.to"
        @click="emit('primary:click', $event)"
      >
        {{ button.slot }}
      </AtomButton>
    </div>
    <div class="video">
      <UiComponentVideo
        :autoplay="video.autoplay"
        :height="video.height"
        :loop="video.loop"
        :muted="video.muted"
        :url="video.url"
        :width="video.width"
      />
    </div>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'primary:click',
])

// Props
const props = defineProps({
  button: {
    default() {
      return {}
    },
    type: Object,
  },
  direction: {
    default: 'left',
    required: false,
    type: String,
    validator(value) {
      return ['left', 'right'].indexOf(value) !== -1
    },
  },
  level: {
    default() {
      return 3
    },
    required: false,
    type: Number,
    validator(value) {
      return [1, 2, 3, 4, 5, 6].indexOf(value) !== -1
    },
  },
  node: {
    default() {
      return undefined
    },
    required: false,
    type: Object,
  },
  video: {
    required: true,
    type: Object,
  },
})

// Composables
const slots = useSlots()

// Computed Properties
const hasButton = computed(() => {
  return !!props.button.slot
})

const hasParagraph = computed(() => {
  const nodes = slots && slots.paragraph && slots.paragraph()
  return nodes && nodes.length > 0
})

// Methods
function renderNode() {
  return h(props.node.component, props.node.properties)
}
</script>

<style lang="postcss" scoped>
.text-video {
  @apply flex flex-col w-full;

  @screen lg {
    @apply items-center;
  }

  &.left {
    @screen lg {
      @apply flex-row-reverse;
    }

    .text {
      @screen xl {
        @apply pl-28;
      }
    }
  }

  &.right {
    @screen lg {
      @apply flex-row;
    }

    .text {
      @screen xl {
        @apply pr-28;
      }
    }
  }

  .video,
  .text {
    @apply w-full;
  }

  .video {
    @apply mt-8 rounded-xl overflow-hidden;

    @screen md {
      @apply max-w-2xl;
    }

    @screen xl {
      @apply mt-0 max-w-full;
      width: 620px;
    }
  }

  .text {
    @screen xl {
      width: 620px;
    }

    .h3 {
      @apply font-medium text-2xl text-winter-green-900 leading-8 !important;

      @screen mdminus {
        font-size: 28px !important;
        line-height: 34px !important;
      }

      @screen mdplus {
        @apply text-3xl leading-10 !important;
      }
    }

    .description {
      @apply mt-4 text-grey-blue-600 !important;
    }
  }

  .button {
    @apply mt-6;
  }
}
</style>
